import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export type SearchResultType = 
  | "lead" 
  | "order" 
  | "booking" 
  | "supplier" 
  | "product" 
  | "client"
  | "message";

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  meta?: string;
  routeData?: Record<string, string>;
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isLoading: boolean;
  groupedResults: Record<SearchResultType, SearchResult[]>;
}

const SEARCH_DEBOUNCE_MS = 300;

export function useGlobalSearch(): UseGlobalSearchReturn {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["global-search", debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        return [];
      }

      const allResults: SearchResult[] = [];

      // Token-based matching: split the query into words. The database
      // returns rows where ANY word matches ANY field (a single .or()),
      // then we keep only rows where EVERY word appears in at least one
      // field. This lets "UNC GIE" match "UNC MBA GIE Group".
      const tokens = debouncedQuery.toLowerCase().split(/\s+/).filter(Boolean);
      const anyTokenOr = (fields: string[]): string =>
        tokens
          .flatMap((token) => fields.map((f) => `${f}.ilike.%${token}%`))
          .join(",");
      const matchesAllTokens = (
        row: Record<string, unknown>,
        fields: string[]
      ): boolean => {
        const haystack = fields
          .map((f) => String(row[f] ?? ""))
          .join(" ")
          .toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      };

      // Search leads
      const leadFields = ["title", "client_name", "client_email", "inbound_email_id", "client_liaison", "destination", "group_reference"];
      const { data: leadsRaw } = await supabase
        .from("leads")
        .select("id, title, client_name, destination, status, client_email, inbound_email_id, client_liaison, group_reference")
        .or(anyTokenOr(leadFields))
        .limit(50);
      const leads = (leadsRaw || []).filter((l) => matchesAllTokens(l, leadFields)).slice(0, 10);

      if (leads) {
        leads.forEach((lead) => {
          allResults.push({
            id: lead.id,
            type: "lead",
            title: lead.title,
            subtitle: lead.client_name || undefined,
            meta: lead.status || undefined,
          });
        });
      }

      // Search SnapStack orders
      const orderFields = ["order_number", "customer_name"];
      const { data: ordersRaw } = await supabase
        .from("snapstack_orders")
        .select("id, order_number, customer_name, status")
        .or(anyTokenOr(orderFields))
        .limit(50);
      const orders = (ordersRaw || []).filter((o) => matchesAllTokens(o, orderFields)).slice(0, 10);

      if (orders) {
        orders.forEach((order) => {
          allResults.push({
            id: order.id,
            type: "order",
            title: order.order_number,
            subtitle: order.customer_name,
            meta: order.status,
          });
        });
      }

      // Search Coffeebeans bookings
      const bookingFields = ["booking_number", "guest_name", "guest_email", "guest_phone", "experience_name", "payment_reference"];
      const { data: bookingsRaw } = await supabase
        .from("coffeebeans_bookings")
        .select("id, booking_number, guest_name, experience_name, status, brand_id, guest_email, guest_phone, payment_reference, deleted_at")
        .or(anyTokenOr(bookingFields))
        .limit(50);
      const bookings = (bookingsRaw || []).filter((b) => matchesAllTokens(b, bookingFields)).slice(0, 10);

      if (bookings) {
        bookings.forEach((booking) => {
          allResults.push({
            id: booking.id,
            type: "booking",
            title: booking.booking_number,
            subtitle: `${booking.guest_name} - ${booking.experience_name}`,
            meta: booking.deleted_at
              ? `Deleted${booking.status ? ` · ${booking.status}` : ""}`
              : booking.status || undefined,
            routeData: { brand_id: booking.brand_id || "", deleted: booking.deleted_at ? "1" : "" },
          });
        });
      }

      // Search suppliers
      const supplierFields = ["name", "contact_name", "email", "territory"];
      const { data: suppliersRaw } = await supabase
        .from("suppliers")
        .select("id, name, contact_name, territory, email")
        .or(anyTokenOr(supplierFields))
        .limit(50);
      const suppliers = (suppliersRaw || []).filter((s) => matchesAllTokens(s, supplierFields)).slice(0, 10);

      if (suppliers) {
        suppliers.forEach((supplier) => {
          allResults.push({
            id: supplier.id,
            type: "supplier",
            title: supplier.name,
            subtitle: supplier.contact_name || undefined,
            meta: supplier.territory || undefined,
          });
        });
      }

      // Search products
      const productFields = ["product_name", "city", "description"];
      const { data: productsRaw } = await supabase
        .from("products")
        .select("id, product_name, city, description")
        .or(anyTokenOr(productFields))
        .limit(50);
      const products = (productsRaw || []).filter((p) => matchesAllTokens(p, productFields)).slice(0, 10);

      if (products) {
        products.forEach((product) => {
          allResults.push({
            id: product.id,
            type: "product",
            title: product.product_name,
            subtitle: product.city || undefined,
          });
        });
      }

      // Search clients
      const clientFields = ["name", "company", "email"];
      const { data: clientsRaw } = await supabase
        .from("clients")
        .select("id, name, company, email")
        .or(anyTokenOr(clientFields))
        .limit(50);
      const clients = (clientsRaw || []).filter((c) => matchesAllTokens(c, clientFields)).slice(0, 10);

      if (clients) {
        clients.forEach((client) => {
          allResults.push({
            id: client.id,
            type: "client",
            title: client.name,
            subtitle: client.company || undefined,
            meta: client.email || undefined,
          });
        });
      }

      // Search channel messages
      const { data: channelMessagesRaw } = await supabase
        .from("channel_messages")
        .select("id, message, created_at, channel_id, sender_id")
        .or(anyTokenOr(["message"]))
        .order("created_at", { ascending: false })
        .limit(50);
      const channelMessages = (channelMessagesRaw || [])
        .filter((m) => matchesAllTokens(m, ["message"]))
        .slice(0, 10);

      if (channelMessages && channelMessages.length > 0) {
        // Fetch channel names for context
        const channelIds = [...new Set(channelMessages.map(m => m.channel_id))];
        const { data: channelData } = await supabase
          .from("message_channels")
          .select("id, name")
          .in("id", channelIds);
        const channelMap = new Map((channelData || []).map(c => [c.id, c.name]));

        channelMessages.forEach((msg) => {
          const snippet = msg.message.length > 80 ? msg.message.substring(0, 80) + "..." : msg.message;
          allResults.push({
            id: msg.id,
            type: "message",
            title: snippet,
            subtitle: `#${channelMap.get(msg.channel_id) || "channel"}`,
            meta: "channel",
            routeData: { channel_id: msg.channel_id },
          });
        });
      }

      // Search direct messages
      const { data: dmMessagesRaw } = await supabase
        .from("direct_messages")
        .select("id, message, created_at, sender_id, recipient_id")
        .or(anyTokenOr(["message"]))
        .order("created_at", { ascending: false })
        .limit(50);
      const dmMessages = (dmMessagesRaw || [])
        .filter((m) => matchesAllTokens(m, ["message"]))
        .slice(0, 10);

      if (dmMessages && dmMessages.length > 0) {
        const userIds = [...new Set(dmMessages.flatMap(m => [m.sender_id, m.recipient_id]))];
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);
        const profileMap = new Map((profileData || []).map(p => [p.id, p.full_name || p.email || "Unknown"]));

        const { data: sessionData } = await supabase.auth.getSession();
        const currentUserId = sessionData?.session?.user?.id;

        dmMessages.forEach((msg) => {
          const snippet = msg.message.length > 80 ? msg.message.substring(0, 80) + "..." : msg.message;
          const partnerId = msg.sender_id === currentUserId ? msg.recipient_id : msg.sender_id;
          const partnerName = profileMap.get(partnerId) || "Unknown";
          allResults.push({
            id: msg.id,
            type: "message",
            title: snippet,
            subtitle: `DM with ${partnerName}`,
            meta: "dm",
            routeData: { partner_id: partnerId, partner_name: partnerName },
          });
        });
      }

      return allResults;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 1000 * 60, // Cache for 1 minute
  });

  const groupedResults = useMemo(() => {
    const groups: Record<SearchResultType, SearchResult[]> = {
      lead: [],
      order: [],
      booking: [],
      supplier: [],
      product: [],
      client: [],
      message: [],
    };

    results.forEach((result) => {
      groups[result.type].push(result);
    });

    return groups;
  }, [results]);

  return {
    query,
    setQuery,
    results,
    isLoading,
    groupedResults,
  };
}
